import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { useChineseLang } from '../../i18n/chinese'
import { AppUseDispatch, AppUseSelector } from '../../redux/hooks'
import { getWordDetail, wordDoesNotKnow } from '../../redux/actions/storyAction'
import { CHECK_WORD_IS_CLICKED } from '../../redux/types'
import WordDetail from 'containers/word-detail'
import WordExamplesModal from 'containers/modal/word-examples-modal'
import Loader from 'containers/loader/loader'
import ZhWords from 'components/write-speak/ZhText'
import { STORIES, STORY } from 'routes/routeConstant'
import { REGEX } from 'utils/regex'
import { getSentenceWords, getWordLine, wordSimplified } from 'utils/helperFunction'
import { Discussion, DiscussionAnswer, errorCode, fetchDiscussion, markNotificationsRead, wordAdded } from './discussionApi'
import DiscussionList, { useDiscussionThreads } from './DiscussionList'
import { announceNotificationsChanged, ReplyComposer } from './DiscussionBits'
import './discussion.css'

/**
 * A lesson's discussion on its own page (/discussion/:storyId?answer=ref): where the bell and the
 * reply emails lead. Works whether or not the learner's plan still opens the story itself.
 */
const DiscussionPage: React.FC = () => {
  const { t } = useTranslation('discussion')
  const zhLang = useChineseLang()
  const { storyId = '' } = useParams()
  const [search] = useSearchParams()
  const focus = search.get('answer')
  const dispatch = AppUseDispatch()
  const { credentials } = AppUseSelector(({ user }) => user)
  const memberId = credentials?.memberId
  const { wordDetails, isWordClick } = AppUseSelector(({ story }) => story)

  const [discussion, setDiscussion] = useState<Discussion | null>(null)
  const [loading, setLoading] = useState(true)
  const [problem, setProblem] = useState<string | null>(null)

  const load = useCallback(() => {
    if (!storyId) return
    setLoading(true)
    fetchDiscussion(storyId)
      .then((d) => {
        setDiscussion(d)
        setProblem(null)
        // Arriving here from the bell or an email is reading the replies.
        if (d.unread.length) {
          markNotificationsRead(d.unread.map((u) => u.id)).then(announceNotificationsChanged)
          setDiscussion({ ...d, unread: [] })
        }
      })
      .catch((e) => setProblem(errorCode(e) || 'error'))
      .finally(() => setLoading(false))
  }, [storyId])

  useEffect(() => {
    window.scrollTo(0, 0)
    load()
  }, [load])

  const threads = useDiscussionThreads(discussion, (update) => setDiscussion((d) => (d ? update(d) : d)), load, focus)

  // ---------- one-tap dictionary, as on the lesson pages ----------
  const popupRef = useRef<HTMLDivElement>(null)
  const examplesRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState('-1')
  const [wordDetailVisible, setWordDetailVisible] = useState(false)
  const [openExampleModal, setOpenExampleModal] = useState(false)
  const [isImportanceOpen, setImportanceOpen] = useState(false)
  const [isMasteryOpen, setMasteryOpen] = useState(false)
  const lookedUpIn = useRef(new Set<string>())

  const closePopup = () => {
    setWordDetailVisible(false)
    setImportanceOpen(false)
    setMasteryOpen(false)
    setActiveIndex('-1')
  }

  useEffect(() => {
    if (openExampleModal) return
    const onDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (popupRef.current?.contains(target) || examplesRef.current?.contains(target)) return
      // Closing the popup is what counts a look-up, as on the story page.
      if (wordDetailVisible && wordDetails?.word && !isWordClick) {
        wordDoesNotKnow({ word: wordSimplified(wordDetails.word), storyId, page: 'story' } as any)
        dispatch({ type: CHECK_WORD_IS_CLICKED, payload: false })
      }
      if (!target.closest?.('[data-specific-buttons]')) closePopup()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [openExampleModal, wordDetailVisible, isWordClick, wordDetails, storyId, dispatch])

  const onWordTap = (answer: DiscussionAnswer) => (event: any, wordObj: any, sentence: any, key: string) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    const raw = (wordObj?.word || '').replace(/<[^>]+>/g, '')
    if (!raw || wordObj?.plain || REGEX.specialChars.test(raw)) return
    const word = wordSimplified(raw)
    setActiveIndex(activeIndex === key ? '-1' : key)
    if (!memberId) return
    setWordDetailVisible(true)
    getWordDetail({
      detail: {
        word,
        pinyin: wordObj?.pinyin,
        english: wordObj?.english,
        sentenceString: getWordLine(sentence),
        sentenceWords: getSentenceWords(sentence),
      },
      payload: { word, memberId },
      wordType: 'word',
    } as any)
    if (!answer.mine && !lookedUpIn.current.has(answer.ref)) {
      lookedUpIn.current.add(answer.ref)
      wordAdded(answer.ref)
    }
  }

  if (loading && !discussion) return <Loader />

  if (!discussion) {
    return (
      <div className='dc-root dc-page'>
        <h1 className='dc-page-label'>{t('title')}</h1>
        <div className='dc-page-empty'>
          {problem === 'DISCUSSION_ANSWER_FIRST_TO_SEE' || problem === 'DISCUSSION_ANSWER_FIRST' ? (
            <p>
              <Trans t={t} i18nKey='page.answerFirst' components={{ lesson: <Link to={`${STORY}/${storyId}`} /> }} />
            </p>
          ) : problem === 'DISCUSSION_LESSON_GONE' ? (
            <p>
              <Trans t={t} i18nKey='page.notFound' components={{ lessons: <Link to={STORIES} /> }} />
            </p>
          ) : (
            <p>
              <Trans
                t={t}
                i18nKey='page.loadFailed'
                components={{ retry: <button type='button' className='dc-act' onClick={load} /> }}
              />
            </p>
          )}
        </div>
      </div>
    )
  }

  const { replyTo } = threads
  return (
    <>
      <div className='dc-root dc-page'>
        {discussion.title && (
          <div className='dc-page-title' lang={zhLang}>
            {discussion.title}
          </div>
        )}
        <h1 className='dc-page-q' lang={zhLang}>
          {discussion.questionContent?.length ? (
            <ZhWords
              words={discussion.questionContent}
              iterator='DISC-Q-'
              story={{}}
              activeIndex={activeIndex}
              onClickWord={onWordTap({ mine: true } as DiscussionAnswer)}
              fontSize='22px'
              bold
            />
          ) : (
            discussion.question
          )}
        </h1>
        <h2 className='dc-page-label'>{t('title')}</h2>
        {discussion.total === 0 && <p className='dc-muted'>{t('noAnswers')}</p>}
        <DiscussionList threads={threads} story={{}} activeIndex={activeIndex} onWordTap={onWordTap} scrollToRef={focus} />
        {replyTo && (
          <div className='dc-page-foot'>
            <ReplyComposer
              toName={replyTo.mine ? t('composer.yourAnswer') : replyTo.name}
              value={threads.draft}
              onChange={threads.setDraft}
              onCancel={threads.cancelReply}
              onSend={threads.send}
              sending={threads.sending}
              error={threads.error}
              autoFocus
            />
          </div>
        )}
      </div>
      <WordDetail
        ref={popupRef}
        setOpenExampleModal={setOpenExampleModal}
        isImportanceOpen={isImportanceOpen}
        setImportanceOpen={setImportanceOpen}
        isMasteryOpen={isMasteryOpen}
        setMasteryOpen={setMasteryOpen}
        setWordDetailVisible={setWordDetailVisible}
        setActiveIndex={setActiveIndex}
        visible={wordDetailVisible}
        openExampleModal={openExampleModal}
        showMasterySection={true}
      />
      <WordExamplesModal
        ref={examplesRef}
        openExampleModal={openExampleModal}
        setOpenExampleModal={setOpenExampleModal}
        setWordDetailVisible={setWordDetailVisible}
      />
    </>
  )
}

export default DiscussionPage
